import { Tooltip, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

/**
 * Props pour le composant ShortenDisplay
 */
export interface ShortenDisplayProps {
    /** Le libellé à afficher dans le tooltip */
    tooltipLabel?: string;
    /** Le texte à afficher et à copier */
    text?: string | null;
    /** La longueur maximale avant raccourcissement (défaut: 8) */
    shortenLength?: number;
}

/**
 * Composant qui affiche un texte raccourci avec possibilité de copie au clic
 *
 * @example
 * ```tsx
 * <ShortenDisplay
 *   tooltipLabel="ID Utilisateur"
 *   text="123e4567-e89b-12d3-a456-426614174000"
 *   shortenLength={10}
 * />
 * ```
 *
 * @param props - Les propriétés du composant
 * @returns Le composant ShortenDisplay
 */
export const ShortenDisplay: React.FunctionComponent<ShortenDisplayProps> = function ({
    tooltipLabel,
    text = null,
    shortenLength = 8,
}) {
    const { palette } = useTheme();
    const [copied, setCopied] = useState(false);

    const displayValue = useMemo(() => {
        if (!text) return '-';

        const shouldShorten = text.length > shortenLength;
        return shouldShorten ? `${text.slice(0, shortenLength)}...` : text;
    }, [text, shortenLength]);

    const tooltipTitle = useMemo(() => {
        if (copied) {
            return tooltipLabel ? `${tooltipLabel} | Copié !` : 'Copié !';
        }
        return tooltipLabel || 'Copier';
    }, [tooltipLabel, copied]);

    const handleCopy = useCallback(async () => {
        if (!text) {
            console.warn('ShortenDisplay: Aucun texte à copier');
            return;
        }

        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('ShortenDisplay: Erreur lors de la copie:', error);
        }
    }, [text]);

    const isClickable = Boolean(text);

    return (
        <Tooltip
            title={tooltipTitle}
            placement="bottom"
        >
            <Typography
                component="span"
                onClick={isClickable ? handleCopy : undefined}
                sx={{
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'color 0.2s ease',
                    userSelect: 'none',
                    '&:hover': {
                        color: isClickable ? palette.primary.dark : 'inherit',
                    },
                }}
            >
                {displayValue}
            </Typography>
        </Tooltip>
    );
};
